import React, { useState, useRef, useEffect } from 'react';
import './WarmUpDiscussionExercise.css';

const WarmUpDiscussionExercise = ({
  questions, title, level, onClose, updateAttributes,
  isStudent = false, myState: _myState = {}, otherState = {}, updateState = () => {},
}) => {
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [isEditing, setIsEditing] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);
  const [editableTitle, setEditableTitle] = useState(title);
  const [editableLevel, setEditableLevel] = useState(level);
  const [editableQuestions, setEditableQuestions] = useState([...questions]);
  const [showAddMenu, setShowAddMenu] = useState(false);
  const addBtnRef = useRef(null);
  const dotsScrollRef = useRef(null);
  const hintRunRef = useRef(false);
  const [showLeftFade, setShowLeftFade] = useState(false);
  const [showRightFade, setShowRightFade] = useState(false);

  // --- Navigation sync: teacher's position drives the student's view ---
  useEffect(() => {
    if (typeof otherState.currentQuestion !== 'number') return;
    setCurrentQuestionIndex(otherState.currentQuestion);
  }, [otherState.currentQuestion]); // eslint-disable-line

  const handleNext = () => {
    if (currentQuestionIndex < editableQuestions.length - 1) {
      const nextIdx = currentQuestionIndex + 1;
      setCurrentQuestionIndex(nextIdx);
      updateState({ currentQuestion: nextIdx });
    }
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      const prevIdx = currentQuestionIndex - 1;
      setCurrentQuestionIndex(prevIdx);
      updateState({ currentQuestion: prevIdx });
    }
  };

  const goToQuestion = (index) => {
    setCurrentQuestionIndex(index);
    updateState({ currentQuestion: index });
  };

  const handleSave = () => {
    setIsEditing(false);
    if (updateAttributes) {
      updateAttributes({
        questions: editableQuestions,
        title: editableTitle,
        level: editableLevel,
      });
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditableTitle(title);
    setEditableLevel(level);
    setEditableQuestions([...questions]);
  };

  const updateQuestionField = (index, field, value) => {
    setEditableQuestions(prev => prev.map((q, i) => i === index ? { ...q, [field]: value } : q));
  };

  const handleAddEmpty = () => {
    const newQuestion = { question: '', hints: '' };
    setEditableQuestions(prev => {
      const updated = [...prev, newQuestion];
      setCurrentQuestionIndex(updated.length - 1);
      return updated;
    });
    setShowAddMenu(false);
  };

  const handleDeleteQuestion = (questionIndex) => {
    if (editableQuestions.length <= 1) return;

    setEditableQuestions(prev => {
      const updated = prev.filter((_, index) => index !== questionIndex);
      if (currentQuestionIndex >= questionIndex) {
        setCurrentQuestionIndex(Math.max(0, currentQuestionIndex - 1));
      }
      return updated;
    });
  };

  const currentQuestion = editableQuestions[currentQuestionIndex];

  // one-time gentle scroll hint (if dots overflow)
  useEffect(() => {
    if (hintRunRef.current) return;
    if (!dotsScrollRef.current) return;
    if (editableQuestions.length <= 5) return;
    hintRunRef.current = true;
    const scroller = dotsScrollRef.current;
    const delta = 24;
    try {
      const start = scroller.scrollLeft;
      const t1 = setTimeout(() => {
        scroller.scrollTo({ left: start + delta, behavior: 'smooth' });
        const t2 = setTimeout(() => {
          scroller.scrollTo({ left: start, behavior: 'smooth' });
        }, 500);
        (hintRunRef.currentCleanup = () => clearTimeout(t2));
      }, 120);
      return () => {
        clearTimeout(t1);
        if (hintRunRef.currentCleanup) hintRunRef.currentCleanup();
      };
    } catch (_) {}
  }, [editableQuestions.length]);

  // update gradient fades based on scroll position
  useEffect(() => {
    const el = dotsScrollRef.current;
    if (!el) return;
    const update = () => {
      const max = el.scrollWidth - el.clientWidth;
      setShowLeftFade(el.scrollLeft > 2);
      setShowRightFade(el.scrollLeft < max - 2);
    };
    update();
    el.addEventListener('scroll', update);
    return () => el.removeEventListener('scroll', update);
  }, [editableQuestions.length]);

  const perDot = 36; // px
  const gap = 8; // px
  const visibleSlots = Math.min(editableQuestions.length, 6);
  const scrollWidth = visibleSlots > 0 ? Math.round((visibleSlots * perDot) + ((visibleSlots - 1) * gap)) : 0;

  return (
    <div className="wud-exercise">
      <div className="wud-header">
        <div className="wud-header-left">
          <div className="wud-header-icon">
            <svg width="16" height="16" viewBox="0 0 135.2716 291.7073" fill="white">
              <path d="m87.7896 53.271c13.1049 0 23.9709 10.2576 24.7375 23.3508.3564 6.0999-1.6229 11.7529-5.0409 16.2891.8575 2.1729 1.6064 4.1699 2.251 5.9961-9.3091 3.0762-20.5884 7.6074-24.3567 9.1479-.6042-1.1011-1.1774-2.1655-1.853-3.3386-11.5522-20.0562-33.6853-50.3088-71.3037-73.6877-3.7531-2.3315-8.6859-1.1804-11.0175 2.5732-2.3322 3.7524-1.1804 8.6853 2.572 11.0168 33.3844 20.7471 53.6615 47.771 64.6124 66.1646 3.205 5.3821 5.6067 9.9792 7.2937 13.4949.0513.1648.0134.332.0714.4956 1.001 2.8223 23.8037 69.9512-39.6094 144.6387-4.4727 5.2539-3.8281 13.1445 1.4355 17.6172 2.3486 1.9922 5.2246 2.9688 8.0859 2.9688 3.54 0 7.0605-1.4941 9.5312-4.4141 28.0048-32.9773 41.1841-64.9902 46.7474-92.1619l7.5092-.4358c-1.3733 23.7769-5.1587 51.8213-13.4949 82.9883-1.7822 6.6699 2.1777 13.5254 8.8428 15.3125 1.084.2832 2.1729.4199 3.2422.4199 5.5176 0 10.5713-3.6816 12.0654-9.2676 26.7462-99.9854 10.2673-169.5508 8.7946-175.3638-.0018-.0098.0012-.0171-.0006-.0269-.5225-2.4219-13.4326-59.9316-68.2275-105.2051-3.418-2.832-8.4521-2.3438-11.2646 1.0645s-2.334 8.4473 1.0693 11.2598c15.1532 12.5244 26.8073 26.1694 35.7751 39.1553.0214 0 .0409-.0073.0623-.0098.4933-.0293.9822-.0427 1.4699-.0427z"/>
              <path d="m89.0829 100.533c12.4042-.7251 21.8732-11.3696 21.1481-23.7769-.7263-12.4048-11.3727-21.8726-23.7769-21.1462-10.8612.636-19.4678 8.8721-20.9564 19.2371 7.4628 9.0857 13.3459 17.6855 17.8815 25.21 1.845.3685 3.7377.5907 5.7037.476z"/>
            </svg>
          </div>
          {isEditing ? (
            <input
              type="text"
              value={editableTitle}
              onChange={e => setEditableTitle(e.target.value)}
              className="wud-title-input"
              placeholder="Title"
            />
          ) : (
            <span className="wud-title">{editableTitle}</span>
          )}
          {isEditing ? (
            <input
              type="text"
              value={editableLevel}
              onChange={e => setEditableLevel(e.target.value)}
              className="wud-level-input"
              placeholder="Level"
            />
          ) : (
            <span className="wud-level-badge">{editableLevel}</span>
          )}
        </div>
        <div className="wud-header-actions">
          {!isEditing && !isStudent && (
            <button className="wud-icon-btn" aria-label="Edit" data-tip="Edit" onClick={() => setIsEditing(true)}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                <path d="m18.5 2.5 3 3L12 15l-4 1 1-4 9.5-9.5z" />
              </svg>
            </button>
          )}
          {isEditing && (
            <>
              <button className="wud-icon-btn" aria-label="Save" data-tip="Save" onClick={handleSave}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <circle cx="12" cy="12" r="10" />
                  <polyline points="9,12 11,14 15,10" />
                </svg>
              </button>
              <button className="wud-icon-btn" aria-label="Cancel" data-tip="Cancel" onClick={handleCancel}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </>
          )}
          {!isEditing && onClose && (
            <button className="wud-icon-btn" aria-label="Close" data-tip="Close" onClick={onClose}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="eye-icon">
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" className="eye-open" />
                <path d="M2 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" className="eye-closed" style={{ opacity: 0 }} />
                <circle cx="12" cy="12" r="3" className="eye-pupil" />
              </svg>
            </button>
          )}
        </div>
      </div>

      <div className="wud-body">
        {!isStudent && (
          <div className="wud-collapsible">
            <div
              className="wud-collapsible-header"
              onClick={() => setShowInstructions(!showInstructions)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && setShowInstructions(!showInstructions)}
            >
              <span className="wud-collapsible-title">How to use this exercise</span>
              <svg
                className={`wud-collapsible-arrow${showInstructions ? ' is-open' : ''}`}
                width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
              >
                <polyline points="9,18 15,12 9,6"></polyline>
              </svg>
            </div>
            {showInstructions && (
              <div className="wud-collapsible-content">
                <ul className="wud-instructions-list">
                  <li>This is a spoken warm-up — there's no answer box, just prompts to talk about</li>
                  <li>Read the question aloud and let students respond verbally</li>
                  <li>Use the hint (if any) to nudge students who get stuck</li>
                  <li>Click Next to move everyone's screen to the next question together</li>
                  <li>Encourage follow-up questions to keep the conversation going</li>
                </ul>
              </div>
            )}
          </div>
        )}

        <div className="wud-progress">
          <div className="wud-progress-track">
            <div
              className="wud-progress-fill"
              style={{ width: `${((currentQuestionIndex + 1) / editableQuestions.length) * 100}%` }}
            />
          </div>
          <span className="wud-progress-text">
            Question {currentQuestionIndex + 1} of {editableQuestions.length}
          </span>
        </div>

        {currentQuestion && (
          <>
            <div className="wud-prompt-card">
              {isEditing ? (
                <textarea
                  value={currentQuestion.question || ''}
                  onChange={e => updateQuestionField(currentQuestionIndex, 'question', e.target.value)}
                  className="wud-question-input"
                  placeholder="Enter discussion question..."
                  rows={3}
                />
              ) : (
                <p className="wud-prompt-text">{currentQuestion.question || ''}</p>
              )}
            </div>

            {isEditing ? (
              <div className="wud-field">
                <label className="wud-field-label">Hints (optional)</label>
                <textarea
                  value={currentQuestion.hints || ''}
                  onChange={e => updateQuestionField(currentQuestionIndex, 'hints', e.target.value)}
                  placeholder="Add helpful hints or prompts for students..."
                  className="wud-hints-input"
                  rows={2}
                />
              </div>
            ) : currentQuestion.hints && (
              <div className="wud-hints-card">
                <span className="wud-hints-icon">💡</span>
                <p className="wud-hints-text">{currentQuestion.hints}</p>
              </div>
            )}
          </>
        )}

        <div className="wud-nav-row">
          {currentQuestionIndex > 0 ? (
            <button className="wud-nav-btn" onClick={handlePrevious}>Previous</button>
          ) : <div className="wud-nav-placeholder" />}

          <div className="wud-dots-wrap">
            <div
              ref={dotsScrollRef}
              className="wud-dots-scroll"
              style={{
                maxWidth: scrollWidth,
                WebkitMaskImage: (() => {
                  const pad = 24;
                  if (showLeftFade && showRightFade) return `linear-gradient(to right, rgba(0,0,0,0) 0px, #000 ${pad}px, #000 calc(100% - ${pad}px), rgba(0,0,0,0) 100%)`;
                  if (showLeftFade) return `linear-gradient(to right, rgba(0,0,0,0) 0px, #000 ${pad}px, #000 100%)`;
                  if (showRightFade) return `linear-gradient(to right, #000 0%, #000 calc(100% - ${pad}px), rgba(0,0,0,0) 100%)`;
                  return 'none';
                })(),
                maskImage: (() => {
                  const pad = 24;
                  if (showLeftFade && showRightFade) return `linear-gradient(to right, rgba(0,0,0,0) 0px, #000 ${pad}px, #000 calc(100% - ${pad}px), rgba(0,0,0,0) 100%)`;
                  if (showLeftFade) return `linear-gradient(to right, rgba(0,0,0,0) 0px, #000 ${pad}px, #000 100%)`;
                  if (showRightFade) return `linear-gradient(to right, #000 0%, #000 calc(100% - ${pad}px), rgba(0,0,0,0) 100%)`;
                  return 'none';
                })(),
              }}
            >
              {editableQuestions.map((_, index) => (
                <div key={index} className="wud-dot-wrap">
                  <button
                    className={`wud-dot${index === currentQuestionIndex ? ' active' : ''}`}
                    onClick={() => goToQuestion(index)}
                  >
                    {index + 1}
                  </button>
                  {isEditing && editableQuestions.length > 1 && (
                    <button
                      className="wud-dot-delete"
                      onClick={(e) => { e.stopPropagation(); handleDeleteQuestion(index); }}
                      title="Delete question"
                    >
                      ✕
                    </button>
                  )}
                </div>
              ))}
            </div>
            {isEditing && (
              <div className="wud-add-wrap">
                <button
                  ref={addBtnRef}
                  className="wud-add-dot"
                  onClick={() => setShowAddMenu(v => !v)}
                  title="Add question"
                  type="button"
                >
                  +
                </button>
                {showAddMenu && (
                  <div className="wud-add-menu">
                    <button className="wud-add-menu-item" onClick={handleAddEmpty}>Add empty</button>
                  </div>
                )}
              </div>
            )}
          </div>

          {currentQuestionIndex < editableQuestions.length - 1 ? (
            <button className="wud-nav-btn" onClick={handleNext}>Next</button>
          ) : <div className="wud-nav-placeholder" />}
        </div>
      </div>

      {isEditing && (
        <div className="wud-footer">
          <button className="wud-cancel-btn" onClick={handleCancel}>Cancel</button>
          <button className="wud-save-btn" onClick={handleSave}>Save</button>
        </div>
      )}
    </div>
  );
};

export default WarmUpDiscussionExercise;
